/**
 * Utility functions for date and time formatting
 */

/**
 * Format timestamp to a readable date and time string
 * @param {Object|string|number} timestamp - The timestamp to format
 * @returns {string} Formatted date and time string
 */
export const formatTimestamp = (timestamp) => {
  try {
    // Handle different timestamp formats
    let date;
    if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
      // Firestore timestamp format
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp && typeof timestamp === 'string') {
      // ISO string format
      date = new Date(timestamp);
    } else if (timestamp && typeof timestamp === 'number') {
      // Unix timestamp
      date = new Date(timestamp);
    } else {
      return 'Invalid Date';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid Date';
  }
};

/**
 * Format timestamp to a relative time string (e.g., "2 hours ago")
 * @param {Object|string|number} timestamp - The timestamp to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (timestamp) => {
  try {
    let date;
    if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp && typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp && typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'Unknown time';
    }
    
    if (isNaN(date.getTime())) {
      return 'Unknown time';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Unknown time';
  }
};

/**
 * Get camera display name with fallback
 * @param {Object} alert - The alert object
 * @returns {string} Camera display name
 */
export const getCameraDisplayName = (alert) => {
  if (alert.camera_name) {
    return alert.camera_name;
  }
  
  if (alert.camera_id) {
    return `Camera ${alert.camera_id.substring(0, 8)}...`;
  }
  
  return 'Unknown Camera';
};
